import { httpsCallable, functions } from '@/lib/functions';
import { ref, uploadBytes } from 'firebase/storage';
import { doc, collection, setDoc, deleteDoc, Timestamp } from 'firebase/firestore';
import { storage, db, auth } from '@/lib/firebase';
import type { DocumentMetadata, DocumentType } from './documentVault';
import {
  DOCUMENT_ALLOWED_MIME_TYPES,
  MAX_DOCUMENT_SIZE_BYTES,
} from './uploadPolicy';

type ExtractDocumentFieldsRequest = {
  storagePath: string;
  documentType?: DocumentType;
  contentType?: string;
};

/**
 * Extended metadata that can include fields extracted by AI
 * The backend can now extract more fields than the basic DocumentMetadata types
 */
type ExtractedMetadata = Partial<DocumentMetadata> & {
  // Additional fields that AI can extract
  number?: string;
  fullName?: string;
  dateOfBirth?: string;
  nationality?: string;
  issuingCountry?: string;
  issuingAuthority?: string;
  issueDate?: string;
  expirationDate?: string;
  // Visa-specific
  visaType?: string;
  entriesAllowed?: string;
  // License-specific
  licenseClass?: string;
  address?: string;
  // Insurance-specific
  policyNumber?: string;
  provider?: string;
  coverageType?: string;
  coverageStartDate?: string;
  coverageEndDate?: string;
  // Health record
  recordType?: string;
  providerName?: string;
  date?: string;
  notes?: string;
};

type ExtractDocumentFieldsResponse = {
  metadata?: ExtractedMetadata;
  documentName?: string;
  rawText?: string;
  detectedDocumentType?: DocumentType | string;
  parseStatus?: 'parsed' | 'needs_review' | 'failed';
  parseWarnings?: string[];
  fieldConfidence?: Record<string, number>;
  missingRequiredFields?: string[];
  ocrEngine?: string;
  extractionSource?: string;
};

const extractDocumentFieldsFn = httpsCallable<
  ExtractDocumentFieldsRequest,
  ExtractDocumentFieldsResponse
>(functions, 'extractDocumentFields');

export async function extractDocumentFields(
  payload: ExtractDocumentFieldsRequest
): Promise<ExtractDocumentFieldsResponse> {
  const result = await extractDocumentFieldsFn(payload);
  return result.data;
}

/** Firestore path for a document scan job (for onSnapshot). */
export function documentScanJobPath(uid: string, jobId: string): string {
  return `users/${uid}/documentScanJobs/${jobId}`;
}

export type DocumentScanJobResult = {
  jobId: string;
  status: 'processing' | 'parsed' | 'needs_review' | 'failed';
  metadata?: ExtractedMetadata | null;
  documentName?: string | null;
  detectedDocumentType?: string | null;
  rawText?: string | null;
  parseStatus?: 'parsed' | 'needs_review' | 'failed' | null;
  parseWarnings?: string[] | null;
  fieldConfidence?: Record<string, number> | null;
  missingRequiredFields?: string[] | null;
  ocrEngine?: string | null;
  extractionSource?: string | null;
  error?: string | null;
  updatedAt?: unknown;
};

/**
 * Start background document scan (expense-style flow). Uploads to
 * documents/{uid}/_scan/{jobId}.ext; documentScanOcr trigger runs and writes
 * results to users/{uid}/documentScanJobs/{jobId}. Subscribe to that doc for
 * status === 'complete' or 'failed'.
 * Returns jobId and storagePath (use storagePath for callable fallback if job never completes).
 */
export async function startDocumentScan(
  uid: string,
  fileBlob: Blob,
  fileName: string,
  documentType: DocumentType,
  contentType: string
): Promise<{ jobId: string; storagePath: string }> {
  const normalizedContentType = (contentType || '').toLowerCase();
  if (fileBlob.size > MAX_DOCUMENT_SIZE_BYTES) {
    throw new Error('File exceeds 10MB limit');
  }
  if (!DOCUMENT_ALLOWED_MIME_TYPES.has(normalizedContentType)) {
    throw new Error('Unsupported file type for scan');
  }
  const ext = fileName.split('.').pop()?.toLowerCase() || 'bin';
  const safeExt = ['pdf', 'jpg', 'jpeg', 'png', 'heic', 'heif'].includes(ext) ? ext === 'jpeg' ? 'jpg' : ext : 'jpg';
  const jobsRef = collection(db, 'users', uid, 'documentScanJobs');
  const jobRef = doc(jobsRef);
  const jobId = jobRef.id;
  const storagePath = `documents/${uid}/_scan/${jobId}.${safeExt}`;

  const authed = auth.currentUser;
  if (!authed || authed.uid !== uid) {
    throw new Error(
      'Sign-in is required to scan documents. Please sign in again and retry.',
    );
  }

  try {
    // Android: Firestore writes can race ahead of persisted auth if we skip this refresh.
    await authed.getIdToken(true);
  } catch {
    // Non-fatal — proceed; setDoc may still succeed if token was already valid.
  }

  try {
    await setDoc(jobRef, {
      status: 'processing',
      // Client Timestamp keeps Security Rules evaluation predictable vs FieldValue.serverTimestamp()
      // on some RN/Android SDK paths (rules still enforce keys.hasOnly + timestamp type).
      createdAt: Timestamp.now(),
    });
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e);
    const code = typeof e === 'object' && e !== null && 'code' in e ? String((e as { code?: string }).code) : '';
    console.error('[documentOcr] Failed to create document scan job', { code, message: msg });
    const hint =
      code === 'permission-denied'
        ? ' If this persists after signing in again, confirm Firestore rules allow creates on users/{yourUid}/documentScanJobs.'
        : '';
    throw new Error(
      `Could not create scan job (${code || msg}).${hint} Deploy the latest Firestore rules if your project is behind, confirm you are signed in, and try again.`,
    );
  }

  const storageRef = ref(storage, storagePath);
  try {
    await uploadBytes(storageRef, fileBlob, {
      contentType: normalizedContentType,
      customMetadata: {
        documentType,
        contentType: normalizedContentType,
        uploadedBy: uid,
      },
    });
  } catch (e: unknown) {
    await deleteDoc(jobRef).catch(() => {});
    const msg = e instanceof Error ? e.message : String(e);
    const code = typeof e === 'object' && e !== null && 'code' in e ? String((e as { code?: string }).code) : '';
    console.error('[documentOcr] Failed to upload scan source', { code, message: msg });
    throw e instanceof Error ? e : new Error(msg);
  }

  console.log('[documentOcr] Started background scan');
  return { jobId, storagePath };
}
